import React, { useEffect, useState, useMemo, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { Badge, Button } from '@chakra-ui/react';
import { MdDownload } from 'react-icons/md';

import apiRouteConstants from 'constants/apiRouteConstants';
import CallReportColumns from './CallReportColumns';
import InputField from 'components/fields/InputField';
import { SingleDatepicker } from 'components/fields/Datepicker';
import SelectField from 'components/fields/SelectField';
import SearchableSelectField from 'components/fields/SearchableSelectField';
import ExpandableTable from '../GenericExpandableTable';
import GenericModal from 'components/modal/GenericModal';

import { get, getBlob } from 'utils/apiClient';
import { cleanObject } from 'utils/helperFunctions';

const CallReportTable = () => {
  const [filters, setFilters] = useState({ status: "" });
  const [isModalOpen, setModalOpen] = useState(false);
  const [recordingData, setRecordingData] = useState(null);
  const [campaignList, setCampaignList] = useState([]);
  const [targetList, setTargetList] = useState([]);

  // Debounced input updates
  const debouncedUpdateFilters = useMemo(
    () => debounce((updatedFilters) => {
      setFilters((prev) => ({ ...prev, ...updatedFilters }));
    }, 300),
    []
  );

  const fetchCampaignList = useCallback(async () => {
    try {
      const res = await get(apiRouteConstants.campaign.selectList);
      if (res?.data?.length > 0) {
        setCampaignList(res.data.map(camp => ({
          label: camp.camp_name,
          value: camp.camp_name,
        })));
      }
    } catch (err) {
      console.error(err);
    }
  }, []);


  useEffect(() => {
    fetchCampaignList();
  }, [fetchCampaignList]);

  useEffect(() => {
    const fetchTargetList = async () => {
      // Clear target list immediately when campaign is cleared
      if (!filters.campaign) {
        setTargetList([]);
        return;
      }
      
      try {
        const res = await get(apiRouteConstants.target.search, {
          campaignName: filters.campaign,
        });
        if (res?.data?.data?.length > 0) {
          const newTargetList = res.data.data.map(target => ({
            label: `${target.number} (${target.name})`,
            value: target.number,
          }));
          setTargetList(newTargetList);
          
          // Reset target filter if current value doesn't exist in new list (safety check)
          setFilters((prev) => {
            if (prev.target) {
              const targetExists = newTargetList.some(t => t.value === prev.target);
              if (!targetExists) {
                return { ...prev, target: "" };
              }
            }
            return prev;
          });
        } else {
          setTargetList([]);
          // Clear target filter when no targets found
          setFilters((prev) => ({ ...prev, target: "" }));
        }
      } catch (err) {
        console.error(err);
        setTargetList([]);
        setFilters((prev) => ({ ...prev, target: "" }));
      }
    };

    fetchTargetList();
  }, [filters.campaign]);


  const handleDownloadReport = async () => {
    try {
      const filtersToSend = cleanObject(filters);
      const { data } = await getBlob(apiRouteConstants.cdrExport, filtersToSend);
      const blob = new Blob([data], { type: 'text/csv' });
      const link = document.createElement('a');
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = 'call_report.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };

  const exportCallReportsComponent = useMemo(() => (
    <Button
      onClick={handleDownloadReport}
      className='dark:!bg-white/5 dark:border rounded-xl border bg-white/0 p-3 text-sm border-gray-200 dark:!border-white/10 dark:text-white'
    >
      <MdDownload className='mr-2' />
      Export
    </Button>
  ), [filters]);

  const FilterComponents = useMemo(() => (
    <div className='flex gap-4 flex-col md:flex-row md:max-w-[90%] w-full flex-wrap'>
      <SingleDatepicker
        value={filters.fromDate}
        label="From Date"
        placeholder="Select From Date"
        maxDate={new Date()}
        date={filters.fromDate}
        onDateChange={(date) => setFilters({ ...filters, fromDate: date })}
      />
      <SingleDatepicker
        value={filters.toDate}
        label="To Date"
        placeholder="Select To Date"
        maxDate={new Date()}
        date={filters.toDate}
        onDateChange={(date) => setFilters({ ...filters, toDate: date })}
      />
      <InputField
        value={filters.callerID}
        label="Caller ID"
        placeholder="Type Caller ID"
        onChange={(e) => debouncedUpdateFilters({ callerID: e.target.value })}
      />
      <InputField
        value={filters.did}
        label="DID"
        placeholder="Type DID"
        onChange={(e) => debouncedUpdateFilters({ did: e.target.value })}
      />
      <SearchableSelectField
        value={filters.target}
        label="Target"
        flex="1"
        extra="min-w-36"
        options={targetList}
        placeholder="Select Target"
        onChange={(e) =>
          setFilters((prev) => ({ ...prev, target: e.target.value || "" }))
        }
      />
      <SearchableSelectField
        value={filters.campaign}
        label="Campaign"
        flex="1"
        extra="min-w-36"
        options={campaignList}
        placeholder="Select Campaign"
        onChange={(e) => {
          const newCampaign = e.target.value || "";
          // Reset target when campaign changes
          setFilters((prev) => ({ 
            ...prev, 
            campaign: newCampaign,
            target: "" // Always reset target when campaign changes
          }));
        }}
      />
      <SelectField
        value={filters.call_status}
        label="Call Status"
        flex="1"
        extra="min-w-36"
        options={[{ label: "Missed", value: 1 }, { label: "Answered", value: 0 }]}
        placeholder="All"
        onChange={(e) =>
          setFilters({ ...filters, call_status: e.target.value || "" })
        }
      />
    </div>
  ), [filters, targetList, campaignList]);

  const renderExpandedRow = useCallback((row) => {
    const fields = [
      { label: "Call Answered", key: "call_answer" },
      { label: "Caller Carrier", key: "caller_carrier" },
      { label: "Caller Country", key: "caller_country" },
      { label: "Caller Linetype", key: "caller_linetype" },
      { label: "Caller Name", key: "caller_name" }
    ];

    const badges = [
      { label: "Abuse Caller", key: "abuse_caller" },
      { label: "Caller Risky", key: "caller_risky" },
      { label: "Caller Spammer", key: "caller_spammer" },
      { label: "Caller Valid", key: "caller_valid" },
      { label: "Caller Voip", key: "caller_voip" },
    ];

    return (
      <div className='grid grid-cols-3 gap-2 md:grid-cols-6'>
        {fields.map(field =>
          row.original[field.key] && (
            <div key={field.key} className='p-2 flex h-full w-auto items-center justify-around rounded-lg bg-lightPrimary text-navy-700 dark:bg-navy-900 dark:text-white px-3'>
              <b className='text-xs'>{field.label}:</b>
              <span className='pl-1 text-xs'>{row.original[field.key]}</span>
            </div>
          )
        )}
        {badges.map(b =>
          <div key={b.key} className="p-2 flex h-full w-auto items-center justify-around rounded-lg bg-lightPrimary text-navy-700 dark:bg-navy-900 dark:text-white px-3">
            <b className='text-xs'>{b.label}:</b>
            <Badge fontSize="x-small" className="ml-2 p-1 !rounded-lg" colorScheme={row.original[b.key] === 'false' ? 'green' : 'red'}>
              {row.original[b.key] === 'false' ? 'No' : 'Yes'}
            </Badge>
          </div>
        )}
        {row.original.fraudscore && (
          <div className="p-2 flex h-full w-auto items-center justify-around rounded-lg bg-lightPrimary text-navy-700 dark:bg-navy-900 dark:text-white px-3">
            <b className='text-xs'>Fraud Score :</b>
            <Badge fontSize={"x-small"} className="ml-2 p-1 !rounded-lg" colorScheme={Number(row.original.fraudscore) > 50 ? 'red' : 'green'}>
              {row.original.fraudscore}
            </Badge>
          </div>
        )}
      </div>
    );
  }, []);

  const handleShowRecording = useCallback((data) => {
    setRecordingData(data);
    setModalOpen(true);
  }, []);

  return (
    <div>
      <ExpandableTable
        createButton={() =>  exportCallReportsComponent}
        columns={CallReportColumns({ handleShowRecording })}
        fetchUrl={apiRouteConstants.cdr}
        filterComponents={() => FilterComponents}
        renderExpandedRow={renderExpandedRow}
        filterValues={filters}
      />
      <GenericModal isOpen={isModalOpen} title="Recording" onClose={() => { setModalOpen(false); setRecordingData(null); }}>
        <div className='p-4 flex justify-center'>
          <audio controls>
            <source src={recordingData} />
            Your browser does not support the audio element.
          </audio>
        </div>
      </GenericModal>
    </div>
  );
};

export default CallReportTable;
